import { ClockInRestrictionType, EquipmentSelectionOptions, JobCostingPromptOptions } from '__generated__/graphql';
import useJsaReportsEnabled from 'containers/jsa-reports/hooks/useJsaReportsEnabled/useJsaReportsEnabled';
import usePhotosViewCollectionPermissionEnabled from 'containers/photos/hooks/usPhotosViewCollectionPermissionEnabled/usePhotosViewCollectionPermissionEnabled';
import { USE_KIOSK_PERMISSION_TITLE } from 'containers/settings/positions/components/AllowKioskModeFormFields/AllowKioskModeFormFields';
import { POSITION_BREAK_MODE_DETAIL_TITLE } from 'containers/settings/positions/details/BreakModeDetail';
import { POSITION_MANAGE_PROJECT_PLAN_DETAIL_TITLE } from 'containers/settings/positions/details/ManageProjectPlansDetail';
import { positionsValueString } from 'containers/settings/positions/util/util';
import { usePositionQuery } from 'hooks';
import useAutomaticBreaksEnabled from 'hooks/feature/automatic-breaks/useAutomaticBreaksEnabled/useAutomaticBreaksEnabled';
import useProjectPlanningIsEnabled from 'hooks/feature/project-planning/useProjectPlanningIsEnabled/useProjectPlanningIsEnabled';
import useClockInRestrictionTimeConverter from 'hooks/models/position/useClockInRestrictionTimeConverter';
import { isNil } from 'lodash';
import { useCallback } from 'react';
import IPosition from 'types/Position';
import { useFeatureFlags } from 'utils/features';
import { t } from 'utils/localize';
import { positionBreakSettingsStatusStringFromPosition } from 'utils/positionBreakSettingsUtils';
import {
  budgetsStatusString,
  certificationsStatusString,
  equipmentInspectionsStatusString,
  getPhotoVerificationSettings,
  getPhotoVerificationSettingsStatusTitle,
} from 'utils/positionUtils';

export interface IPositionDetailsTableRow {
  permissionText: string;
  positionKey: IPositionDetailKeys;
  positions: IPosition[];
}

export type IPositionDetailKeys =
  | 'editButton'
  | 'scopeByGroup'
  | 'manageEmployees'
  | 'timeEvents'
  | 'manageTimeEntries'
  | 'manageTimeOff'
  | 'manageMediaCollection'
  | 'viewMediaCollection'
  | 'memberLock'
  | 'viewSchedule'
  | 'manageSchedule'
  | 'canManageProjectCostCodeQuantity'
  | 'viewActivityReports'
  | 'manageCompanySettings'
  | 'manageProjects'
  | 'manageCostCodes'
  | 'manageEquipment'
  | 'viewEquipmentLocations'
  | 'viewExpenses'
  | 'manageBudgets'
  | 'viewBudgets'
  | 'manageEmployeeGroups'
  | 'dailyProjectPrompts'
  | 'dailyProjectPromptsUseSubprojects'
  | 'clockInRestriction'
  | 'equipmentSelectionRequirement'
  | 'photoVerificationSettings'
  | 'jobCostingPrompt'
  | 'manageBulletinPosts'
  | 'canRequestTimeOff'
  | 'managePerDiem'
  | 'manageIncident'
  | 'createIncident'
  | 'performInspections'
  | 'manageInspections'
  | 'manageObservations'
  | 'reportObservations'
  | 'performTraining'
  | 'manageTraining'
  | 'useKiosk'
  | 'createSafetyAnalysisReports'
  | 'manageSafetyAnalysisReports'
  | 'breakSettings'
  | 'certifications'
  | 'manageCertificates'
  | 'equipmentInspections'
  | 'manageResourcePlanning'
  | 'requireTimeEntryDescription';

export default function usePositionDetailsTableData() {
  const { positions, refetch, loading } = usePositionQuery();
  const { fromClockInRestriction } = useClockInRestrictionTimeConverter();
  const isProgressEnabled = useFeatureFlags('WORK_ACCOMPLISHED');
  const isPerDiemEnabled = useFeatureFlags('PER_DIEM');
  const dailyReportIsEnabled = useFeatureFlags('DAILY_REPORTS');
  const isPro = useFeatureFlags('PRO');
  const isEssential = useFeatureFlags('ESSENTIAL');
  const canEditPositions = isPro || isEssential;
  const isEquipmentPositionSettingEnabled = useFeatureFlags('REQUIRE_EQUIPMENT_POSITION_SETTING');
  const isSafetyIncidentsEnabled = useFeatureFlags('SAFETY_INCIDENTS');
  const isSafetyInspectionsEnabled = useFeatureFlags('SAFETY_INSPECTIONS');
  const isSafetyObservationsEnabled = useFeatureFlags('SAFETY_OBSERVATIONS');
  const isSafetyTrainingsEnabled = useFeatureFlags('SAFETY_TRAINING');
  const isUseKioskPermissionEnabled = useFeatureFlags('USE_KIOSK_PERMISSION');
  const isCertificationsEnabled = useFeatureFlags('CERTIFICATIONS');
  const isEquipmentInspectionsEnabled = useFeatureFlags('EQUIPMENT_INSPECTIONS');
  const photosViewCollectionPermissionEnabled = usePhotosViewCollectionPermissionEnabled();
  const isJsaReportsEnabled = useJsaReportsEnabled();
  const isPositionBreakSettingsEnabled = useAutomaticBreaksEnabled();
  const isProjectPlanningIsEnabled = useProjectPlanningIsEnabled();

  const getRows = useCallback(() => {
    const data: IPositionDetailsTableRow[] = [];

    if (canEditPositions) {
      data.push({
        permissionText: '',
        positionKey: 'editButton',
        positions: positions,
      });
    }

    data.push({
      permissionText: t('Scope to Group*'),
      positionKey: 'scopeByGroup',
      positions: positions,
    });

    data.push({
      permissionText: t('Manage Employees for'),
      positionKey: 'manageEmployees',
      positions: positions,
    });

    data.push({
      permissionText: t('Track Time for'),
      positionKey: 'timeEvents',
      positions: positions,
    });

    data.push({
      permissionText: t('Edit Time Entries for'),
      positionKey: 'manageTimeEntries',
      positions: positions,
    });

    data.push({
      permissionText: t('Manage Time Off for'),
      positionKey: 'manageTimeOff',
      positions: positions,
    });

    data.push({
      permissionText: t('Manage Photos Posted by*'),
      positionKey: 'manageMediaCollection',
      positions: positions,
    });

    if (photosViewCollectionPermissionEnabled) {
      data.push({
        permissionText: t('View Photos Posted by*'),
        positionKey: 'viewMediaCollection',
        positions: positions,
      });
    }

    data.push({
      permissionText: t('Manage Lock Dates for'),
      positionKey: 'memberLock',
      positions: positions,
    });

    data.push({
      permissionText: t('View Schedules for*'),
      positionKey: 'viewSchedule',
      positions: positions,
    });

    data.push({
      permissionText: t('Manage Schedules for*'),
      positionKey: 'manageSchedule',
      positions: positions,
    });

    if (isProgressEnabled) {
      data.push({
        permissionText: t('Manage Progress**'),
        positionKey: 'canManageProjectCostCodeQuantity',
        positions: positions,
      });
    }

    if (isPerDiemEnabled) {
      data.push({
        permissionText: t('Manage Per Diem*'),
        positionKey: 'managePerDiem',
        positions: positions,
      });
    }

    data.push({
      permissionText: t('View Activity Reports for*'),
      positionKey: 'viewActivityReports',
      positions: positions,
    });

    data.push({
      permissionText: t('Manage Company Settings'),
      positionKey: 'manageCompanySettings',
      positions: positions,
    });

    data.push({
      permissionText: t('Manage Projects'),
      positionKey: 'manageProjects',
      positions: positions,
    });

    data.push({
      permissionText: t('Manage Cost Codes'),
      positionKey: 'manageCostCodes',
      positions: positions,
    });

    data.push({
      permissionText: t('Manage Equipment'),
      positionKey: 'manageEquipment',
      positions: positions,
    });

    data.push({
      permissionText: t('View Equipment GPS'),
      positionKey: 'viewEquipmentLocations',
      positions: positions,
    });

    if (isEquipmentInspectionsEnabled) {
      data.push({
        permissionText: t('Equipment Inspections'),
        positionKey: 'equipmentInspections',
        positions: positions,
      });
    }

    if (isCertificationsEnabled) {
      data.push({
        permissionText: t('Certifications'),
        positionKey: 'certifications',
        positions: positions,
      });
    }

    data.push({
      permissionText: t('View Expenses'),
      positionKey: 'viewExpenses',
      positions: positions,
    });

    data.push({
      permissionText: t('Budgets*'),
      positionKey: 'manageBudgets',
      positions: positions,
    });

    data.push({
      permissionText: t('Manage Employee Groups'),
      positionKey: 'manageEmployeeGroups',
      positions: positions,
    });

    if (isProjectPlanningIsEnabled) {
      data.push({
        permissionText: `${POSITION_MANAGE_PROJECT_PLAN_DETAIL_TITLE}*`,
        positionKey: 'manageResourcePlanning',
        positions,
      });
    }

    if (isSafetyIncidentsEnabled) {
      data.push({
        permissionText: t('Create Incident*'),
        positionKey: 'createIncident',
        positions: positions,
      });

      data.push({
        permissionText: t('Manage Incident*'),
        positionKey: 'manageIncident',
        positions: positions,
      });
    }

    if (isSafetyInspectionsEnabled) {
      data.push({
        permissionText: t('Perform Inspections*'),
        positionKey: 'performInspections',
        positions: positions,
      });

      data.push({
        permissionText: t('Manage Inspections*'),
        positionKey: 'manageInspections',
        positions: positions,
      });
    }

    if (isSafetyObservationsEnabled) {
      data.push({
        permissionText: t('Report Observations*'),
        positionKey: 'reportObservations',
        positions,
      });

      data.push({
        permissionText: t('Manage Observations'),
        positionKey: 'manageObservations',
        positions,
      });
    }

    if (isSafetyTrainingsEnabled) {
      data.push({
        permissionText: t('Perform Trainings*'),
        positionKey: 'performTraining',
        positions,
      });

      data.push({
        permissionText: t('Manage Trainings*'),
        positionKey: 'manageTraining',
        positions,
      });
    }

    if (isJsaReportsEnabled) {
      data.push({
        permissionText: t('Create JSA Reports*'),
        positionKey: 'createSafetyAnalysisReports',
        positions,
      });

      data.push({
        permissionText: t('Manage JSA Reports*'),
        positionKey: 'manageSafetyAnalysisReports',
        positions,
      });
    }

    if (dailyReportIsEnabled) {
      data.push({
        permissionText: t('Receive Field Report Prompts*'),
        positionKey: 'dailyProjectPrompts',
        positions: positions,
      });

      data.push({
        permissionText: t('Assign Reports to Subprojects*'),
        positionKey: 'dailyProjectPromptsUseSubprojects',
        positions: positions,
      });
    }

    data.push({
      permissionText: t('Clock In Restriction*'),
      positionKey: 'clockInRestriction',
      positions: positions,
    });

    data.push({
      permissionText: t('Require Time Entry Description'),
      positionKey: 'requireTimeEntryDescription',
      positions: positions,
    });

    if (isEquipmentPositionSettingEnabled) {
      data.push({
        permissionText: t('Require Equipment (Clock Only)*'),
        positionKey: 'equipmentSelectionRequirement',
        positions: positions,
      });
    }

    data.push({
      permissionText: t('Photo Verification*'),
      positionKey: 'photoVerificationSettings',
      positions: positions,
    });

    data.push({
      permissionText: t('Job Costing Prompt*'),
      positionKey: 'jobCostingPrompt',
      positions: positions,
    });

    data.push({
      permissionText: t('Bulletin**'),
      positionKey: 'manageBulletinPosts',
      positions: positions,
    });

    data.push({
      permissionText: t('Can Request Time Off'),
      positionKey: 'canRequestTimeOff',
      positions: positions,
    });

    if (isUseKioskPermissionEnabled) {
      data.push({
        permissionText: USE_KIOSK_PERMISSION_TITLE,
        positionKey: 'useKiosk',
        positions,
      });
    }

    if (isPositionBreakSettingsEnabled) {
      data.push({
        permissionText: POSITION_BREAK_MODE_DETAIL_TITLE,
        positionKey: 'breakSettings',
        positions,
      });
    }

    return data;
  }, [
    isPro,
    positions,
    photosViewCollectionPermissionEnabled,
    isProgressEnabled,
    isPerDiemEnabled,
    isEquipmentInspectionsEnabled,
    isCertificationsEnabled,
    isSafetyIncidentsEnabled,
    isSafetyInspectionsEnabled,
    isSafetyObservationsEnabled,
    isSafetyTrainingsEnabled,
    isJsaReportsEnabled,
    dailyReportIsEnabled,
    isEquipmentPositionSettingEnabled,
    isUseKioskPermissionEnabled,
    isPositionBreakSettingsEnabled,
    isProjectPlanningIsEnabled,
  ]);

  function getPositionValue(row: IPositionDetailsTableRow, positionId: string) {
    const position = row.positions.find((p) => p.id === positionId);

    const equipmentSelectionRequirement = position?.equipmentSelectionRequirement ?? EquipmentSelectionOptions.Off;

    switch (row.positionKey) {
      case 'scopeByGroup':
        return position?.scopeByGroup === true ? t('Yes') : t('No');
      case 'manageEmployees':
        return positionsValueString(position?.manageEmployees ?? 0);
      case 'timeEvents':
        return positionsValueString(position?.timeEvents ?? 0);
      case 'manageTimeEntries':
        return positionsValueString(position?.manageTimeEntries ?? 0);
      case 'manageTimeOff':
        return positionsValueString(position?.manageTimeOff ?? 0);
      case 'manageMediaCollection':
        return positionsValueString(position?.manageMediaCollection ?? 0);
      case 'viewMediaCollection':
        return positionsValueString(position?.viewMediaCollection ?? 0);
      case 'memberLock':
        return positionsValueString(position?.memberLock ?? 0);
      case 'viewSchedule':
        return positionsValueString(position?.viewSchedule ?? 0);
      case 'manageSchedule':
        return positionsValueString(position?.manageSchedule ?? 0);
      case 'canManageProjectCostCodeQuantity':
        return positionsValueString(position?.canManageProjectCostCodeQuantity ?? 0);
      case 'viewActivityReports':
        if (position?.viewAllActivityReport === true) {
          return t('Everyone (All Activity Reports)');
        } else {
          return positionsValueString(position?.viewMemberActivityReport ?? 0);
        }
      case 'manageCompanySettings':
        return position?.manageCompanySettings === true ? t('Yes') : t('No');
      case 'manageProjects':
        return position?.manageProjects === true ? t('Yes') : t('No');
      case 'manageCostCodes':
        return position?.manageCostCodes === true ? t('Yes') : t('No');
      case 'manageEquipment':
        return position?.manageEquipment === true ? t('Yes') : t('No');
      case 'viewEquipmentLocations':
        return position?.viewEquipmentLocations === true ? t('Yes') : t('No');
      case 'equipmentInspections':
        return equipmentInspectionsStatusString(position);
      case 'viewExpenses':
        if (position?.viewMemberExpenses === true && position?.viewProjectExpenses === true) {
          return t('Yes');
        }
        return t('No');
      case 'manageBudgets':
        return budgetsStatusString(position);
      case 'manageEmployeeGroups':
        return position?.manageEmployeeGroups === true ? t('Yes') : t('No');
      case 'manageIncident':
        return position?.manageIncident === true ? t('Yes') : t('No');
      case 'createIncident':
        return position?.createIncident === true ? t('Yes') : t('No');
      case 'performInspections':
        return position?.performInspections === true ? t('Yes') : t('No');
      case 'manageInspections':
        return position?.manageInspections === true ? t('Yes') : t('No');
      case 'performTraining':
        return position?.performTraining === true ? t('Yes') : t('No');
      case 'manageTraining':
        return position?.manageTraining === true ? t('Yes') : t('No');
      case 'manageObservations':
        return position?.manageObservations === true ? t('Yes') : t('No');
      case 'reportObservations':
        return position?.reportObservations === true ? t('Yes') : t('No');
      case 'createSafetyAnalysisReports':
        return position?.createSafetyAnalysisReports === true ? t('Yes') : t('No');
      case 'manageSafetyAnalysisReports':
        return position?.manageSafetyAnalysisReports === true ? t('Yes') : t('No');
      case 'dailyProjectPrompts':
        return position?.dailyProjectPrompts === true ? t('Yes') : t('No');
      case 'certifications':
        return certificationsStatusString(position);
      case 'dailyProjectPromptsUseSubprojects':
        if (position?.dailyProjectPrompts === true) {
          return position?.dailyProjectPromptsUseSubprojects === true ? t('Yes') : t('No');
        } else {
          return '---';
        }
      case 'photoVerificationSettings':
        if (!isNil(position)) {
          const photoVerificationSettings = getPhotoVerificationSettings(position);
          return getPhotoVerificationSettingsStatusTitle(photoVerificationSettings);
        } else {
          return '---';
        }
      case 'jobCostingPrompt':
        if (!isNil(position)) {
          switch (position.jobCostingPrompt) {
            case JobCostingPromptOptions.Off:
              return t('Off');
            case JobCostingPromptOptions.On:
              return t('On');
            case JobCostingPromptOptions.Required:
              return t('On (Required)');
          }
        }
        return t('Off');
      case 'clockInRestriction':
        if (!isNil(position?.clockInRestriction)) {
          switch (position?.clockInRestriction) {
            case ClockInRestrictionType.Off:
              return t('Off');
            case ClockInRestrictionType.Schedule:
              return t('Schedule');
            case ClockInRestrictionType.Time:
              return (
                t('Start Time') + ` (${fromClockInRestriction(position.clockInRestrictionStartTime).toFormat('t')})`
              );
          }
        }
        return '---';
      case 'manageBulletinPosts':
        return position?.manageBulletinPosts === true ? t('Yes') : t('No');
      case 'canRequestTimeOff':
        return position?.canRequestTimeOff === true ? t('Yes') : t('No');
      case 'manageResourcePlanning':
        return position?.manageResourcePlanning === true ? t('Yes') : t('No');
      case 'managePerDiem':
        return positionsValueString(position?.managePerDiem ?? 0);
      case 'useKiosk':
        return position?.useKiosk === true ? t('Yes') : t('No');
      case 'requireTimeEntryDescription':
        return position?.requireTimeEntryDescription === true ? t('Yes') : t('No');
      case 'breakSettings':
        return position ? positionBreakSettingsStatusStringFromPosition(position) : '---';
      case 'equipmentSelectionRequirement':
        switch (equipmentSelectionRequirement) {
          case EquipmentSelectionOptions.Off:
            return t('Off');
          case EquipmentSelectionOptions.Recommended:
            return t('Opt-Out Required');
          case EquipmentSelectionOptions.Required:
            return t('Require Equipment');
        }
    }
  }

  return { getRows, getPositionValue, refetch, positions, loading };
}
